// src/pages/Sanitary/SanitaryRuleEditorPage.jsx
// =============================================================================
// Single professional editor for sanitary consumption rules.
// =============================================================================
// Users edit one business object. Technical rule header/version boundaries are
// enforced by the backend, but are not exposed as competing inline forms.

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AuthContext from "../../context/AuthContext";
import { fetchCatalogServices } from "../../services/servicesCatalogService";
import { fetchStockLocations } from "../../services/stockService";
import { fetchUnitsOfMeasure } from "../../services/unitsOfMeasureService";
import { listSanitaryProducts } from "../../services/sanitaryProductService";
import {
  SANITARY_DAILY_PRORATION_MODES,
  SANITARY_QUANTITY_BASES,
  SANITARY_RULE_STATUSES,
  SANITARY_RULE_TYPES,
  createSanitaryRule,
  deleteSanitaryRule,
  getSanitaryRule,
  saveSanitaryRuleEditor,
} from "../../services/sanitaryRuleService";
import {
  fetchSanitarySolutionDefinitions,
  previewSanitarySolutionPreparation,
} from "../../services/sanitarySolutionService";

import "../Stock/StockMasterDataPage.css";
import "./SanitaryProductsPage.css";
import {
  canEditRuleConsumptionIdentity,
  ruleConsumptionIdentityChanged,
  switchRuleConsumptionBasis,
} from "./sanitaryRuleEditorIdentity";
import "./SanitaryRulesPage.css";

const RULE_TYPES = Object.values(SANITARY_RULE_TYPES);
const STATUSES = Object.values(SANITARY_RULE_STATUSES);
const todayIso = () => new Date().toISOString().slice(0, 10);

function normalizeRole(role) {
  return String(role || "").trim().toLowerCase();
}
function asArray(value) { return Array.isArray(value) ? value : []; }
function formatQty(value) {
  if (value === undefined || value === null || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 6 }) : String(value);
}
function productLabel(rowOrProduct) {
  const p = rowOrProduct?.product || rowOrProduct || {};
  return [p.name_public, p.variant_label, p.sku ? `SKU ${p.sku}` : null].filter(Boolean).join(" · ") || "—";
}
function locationLabel(location) {
  if (!location) return "—";
  return [location.code, location.name].filter(Boolean).join(" · ") || "—";
}
function definitionLabel(definition) {
  const v = definition?.currentVersion;
  return definition
    ? `${definition.name}${v ? ` · ${formatQty(v.solution_volume)} ${v.solution_unit_symbol || v.solution_unit_code} / ${formatQty(v.concentration_percent)}%` : ""}`
    : "—";
}
function nextDayIso(dateIso) {
  const base = /^\d{4}-\d{2}-\d{2}$/.test(String(dateIso || "")) ? String(dateIso) : todayIso();
  const d = new Date(`${base}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
function maxIso(a, b) {
  return String(a || "") >= String(b || "") ? a : b;
}
function supportsDailyProration(ruleType) {
  return ruleType === SANITARY_RULE_TYPES.WORKING_DAY || ruleType === SANITARY_RULE_TYPES.PER_STATION;
}

function emptyForm() {
  return {
    code: "",
    name: "",
    rule_type: SANITARY_RULE_TYPES.WORKING_DAY,
    status: SANITARY_RULE_STATUSES.DRAFT,
    product_id: "",
    requires_confirmation: true,
    posts_to_stock: true,
    comment: "",
    valid_from: todayIso(),
    valid_to: "",
    quantity_basis: SANITARY_QUANTITY_BASES.DIRECT_PRODUCT,
    quantity: "",
    unit_code: "",
    solution_definition_id: "",
    solution_execution_count: "1",
    location_id: "",
    service_id: "",
    station_code: "",
    station_label: "",
    calendar_policy: "working_days_only",
    visit_status_policy: "completed_or_checked",
    daily_proration_mode: SANITARY_DAILY_PRORATION_MODES.NONE,
    rule_config_json: null,
    version_comment: "",
  };
}

function formFromRule(rule) {
  const v = rule?.latestVersion || {};
  return {
    code: rule?.code || "",
    name: rule?.name || "",
    rule_type: rule?.rule_type || SANITARY_RULE_TYPES.WORKING_DAY,
    status: rule?.status || SANITARY_RULE_STATUSES.DRAFT,
    product_id: rule?.product_id || "",
    requires_confirmation: Boolean(rule?.requires_confirmation),
    posts_to_stock: Boolean(rule?.posts_to_stock),
    comment: rule?.comment || "",
    valid_from: v.valid_from || todayIso(),
    valid_to: v.valid_to || "",
    quantity_basis: v.quantity_basis || SANITARY_QUANTITY_BASES.DIRECT_PRODUCT,
    quantity: v.quantity ?? "",
    unit_code: v.unit_code || rule?.product?.stock_unit_code || "",
    solution_definition_id: v.solution_definition_id || "",
    solution_execution_count: v.solution_execution_count ?? "1",
    location_id: v.location_id || rule?.location_id || "",
    service_id: v.service_id || "",
    station_code: v.station_code || "",
    station_label: v.station_label || "",
    calendar_policy: v.calendar_policy || "working_days_only",
    visit_status_policy: v.visit_status_policy || "completed_or_checked",
    daily_proration_mode: v.daily_proration_mode || SANITARY_DAILY_PRORATION_MODES.NONE,
    rule_config_json: v.rule_config_json || null,
    version_comment: v.comment || "",
  };
}

function versionFingerprint(form = {}) {
  const payload = {
    valid_from: form.valid_from || null,
    valid_to: form.valid_to || null,
    quantity_basis: form.quantity_basis || null,
    // A product-only edit must use the atomic version editor, not header_only.
    product_id: form.quantity_basis === SANITARY_QUANTITY_BASES.WORKING_SOLUTION ? null : String(form.product_id || ""),
    quantity: form.quantity === "" ? null : String(form.quantity ?? ""),
    unit_code: form.unit_code || null,
    solution_definition_id: form.solution_definition_id || null,
    solution_execution_count: form.solution_execution_count === "" ? null : String(form.solution_execution_count ?? ""),
    location_id: form.location_id || null,
    service_id: form.service_id || null,
    station_code: form.station_code || null,
    station_label: form.station_label || null,
    calendar_policy: form.calendar_policy || null,
    visit_status_policy: form.visit_status_policy || null,
    daily_proration_mode: form.daily_proration_mode || null,
    rule_config_json: form.rule_config_json || null,
    version_comment: form.version_comment || null,
  };
  return JSON.stringify(payload);
}

function newVersionForm(rule) {
  const form = formFromRule(rule);
  const lastUsed = rule?.latestVersion?.usage?.last_used_business_date || null;
  const validTo = rule?.latestVersion?.valid_to || null;
  const baseline = maxIso(maxIso(todayIso(), lastUsed || todayIso()), validTo || todayIso());
  return {
    ...form,
    valid_from: nextDayIso(baseline),
    valid_to: "",
    version_comment: "",
  };
}

function EditorSection({ number, title, subtitle, children }) {
  return (
    <section className="sanitary-rule-editor-section">
      <div className="sanitary-rule-editor-section__heading">
        <span className="sanitary-rule-editor-section__number">{number}</span>
        <div>
          <h2>{title}</h2>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
      </div>
      <div className="sanitary-rule-editor-section__body">{children}</div>
    </section>
  );
}

export default function SanitaryRuleEditorPage() {
  const { t } = useTranslation(["sanitary", "common"]);
  const navigate = useNavigate();
  const { ruleId } = useParams();
  const isNew = !ruleId;
  const { authState } = useContext(AuthContext);
  const role = normalizeRole(authState?.user?.role);
  const canEdit = role === "superuser" || role === "reception";

  const [rule, setRule] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [units, setUnits] = useState([]);
  const [services, setServices] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [newVersionMode, setNewVersionMode] = useState(false);
  const [solutionPreview, setSolutionPreview] = useState(null);
  const [solutionPreviewError, setSolutionPreviewError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    Promise.all([
      listSanitaryProducts({ profiled_only: "true", product_active: "true", limit: 500 }),
      fetchStockLocations({ active: true }),
      fetchUnitsOfMeasure({ active: true }),
      fetchCatalogServices({ active: true, limit: 500 }),
      fetchSanitarySolutionDefinitions({ limit: 500, business_date: todayIso() }),
      isNew ? Promise.resolve(null) : getSanitaryRule(ruleId),
    ])
      .then(([productRows, locationRows, unitRows, serviceRows, definitionRows, loadedRule]) => {
        if (!active) return;
        setProducts(asArray(productRows).filter((row) => row.profile?.is_active !== false));
        setLocations(asArray(locationRows));
        setUnits(asArray(unitRows));
        setServices(asArray(serviceRows));
        setDefinitions(asArray(definitionRows));
        if (loadedRule) {
          setRule(loadedRule);
          setForm(formFromRule(loadedRule));
        } else {
          setForm(emptyForm());
        }
      })
      .catch((err) => {
        if (active) setError(err?.message || t("sanitary:rules.editor.loadFailed"));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isNew, ruleId, t]);

  const latestVersion = rule?.latestVersion || null;
  const currentVersionEditable = isNew || Boolean(latestVersion?.usage?.can_edit);
  const versionLocked = !isNew && !newVersionMode && !currentVersionEditable;
  const ruleTypeLocked = !isNew;
  const identityLocked = !canEditRuleConsumptionIdentity(rule, { isNew, newVersionMode });
  const identityChanged = !isNew && ruleConsumptionIdentityChanged(form, rule);
  const workingSolution = form.quantity_basis === SANITARY_QUANTITY_BASES.WORKING_SOLUTION;

  const selectedProduct = useMemo(() => {
    const row = products.find((item) => String(item.product?.id || item.id) === String(form.product_id));
    return row?.product || row || (String(rule?.product?.id || "") === String(form.product_id || "") ? rule?.product : null) || null;
  }, [products, form.product_id, rule]);

  function patch(partial) {
    setForm((prev) => {
      if (partial.quantity_basis !== undefined && identityLocked) return prev;
      const base = partial.quantity_basis !== undefined
        ? switchRuleConsumptionBasis(prev, partial.quantity_basis)
        : prev;
      const next = { ...base, ...partial };
      if (partial.product_id !== undefined) {
        const row = products.find((item) => String(item.product?.id || item.id) === String(partial.product_id));
        const product = row?.product || row;
        next.unit_code = product?.stock_unit_code || "";
      }
      if (partial.rule_type && partial.rule_type !== SANITARY_RULE_TYPES.PER_SERVICE) next.service_id = "";
      if (partial.rule_type && partial.rule_type !== SANITARY_RULE_TYPES.PER_STATION) {
        next.station_code = "";
        next.station_label = "";
      }
      return next;
    });
  }

  useEffect(() => {
    let active = true;
    setSolutionPreview(null);
    setSolutionPreviewError(null);
    const timer = window.setTimeout(() => {
      if (!workingSolution || !form.solution_definition_id || !form.valid_from) return;
      previewSanitarySolutionPreparation({
        solution_definition_id: form.solution_definition_id,
        execution_count: form.solution_execution_count || 1,
        prepared_at: `${form.valid_from}T12:00`,
      })
        .then((preview) => {
          if (!active) return;
          setSolutionPreview(preview);
          if (!form.location_id && preview?.defaultLocationId) {
            setForm((prev) => ({ ...prev, location_id: String(preview.defaultLocationId) }));
          }
        })
        .catch((err) => {
          if (active) setSolutionPreviewError(err?.message || t("sanitary:rules.form.solutionPreviewFailed"));
        });
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [workingSolution, form.solution_definition_id, form.solution_execution_count, form.valid_from, form.location_id, t]);

  function startNewVersion() {
    if (!rule) return;
    setForm(newVersionForm(rule));
    setNewVersionMode(true);
    setError(null);
    setMessage(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function cancelNewVersion() {
    setNewVersionMode(false);
    setForm(formFromRule(rule));
    setError(null);
    setMessage(null);
  }

  async function createRuleWithStatus(status) {
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const created = await createSanitaryRule({ ...form, status });
      navigate(`/sanitary/rules/${created.id}`, { replace: true });
    } catch (err) {
      setError(err?.message || t("sanitary:rules.messages.createFailed"));
    } finally {
      setSaving(false);
    }
  }

  async function saveExisting(event) {
    event.preventDefault();
    if (!rule || !canEdit || saving) return;
    if (identityChanged && !identityLocked && (rule.versions || []).length > 1) {
      const basisChanged = form.quantity_basis !== (latestVersion?.quantity_basis || SANITARY_QUANTITY_BASES.DIRECT_PRODUCT);
      const confirmed = window.confirm(t(
        basisChanged ? "sanitary:rules.editor.confirmBasisChangeAllUnused" : "sanitary:rules.editor.confirmProductChangeAllUnused",
        { count: rule.versions.length }
      ));
      if (!confirmed) return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const versionChanged = versionFingerprint(form) !== versionFingerprint(formFromRule(rule));
      const editorMode = newVersionMode
        ? "new_version"
        : currentVersionEditable && versionChanged
          ? "update_current"
          : "header_only";
      const updated = await saveSanitaryRuleEditor(rule.id, {
        ...form,
        editor_mode: editorMode,
        version_id: latestVersion?.id || null,
      });
      setRule(updated);
      setForm(formFromRule(updated));
      setNewVersionMode(false);
      setMessage(
        editorMode === "new_version"
          ? t("sanitary:rules.messages.versionCreated")
          : t("sanitary:rules.messages.updated")
      );
    } catch (err) {
      const conflictKeys = {
        SANITARY_RULE_IDENTITY_ALREADY_USED: "identityConflictUsed",
        SANITARY_RULE_VERSION_ALREADY_USED: "identityConflictVersionUsed",
        SANITARY_RULE_EDITOR_STALE: "identityConflictStale",
        SANITARY_RULE_IDENTITY_EDIT_REQUIRES_CURRENT: "identityConflictMode",
      };
      const conflictKey = conflictKeys[err?.code];
      setError(conflictKey ? t(`sanitary:rules.editor.${conflictKey}`) : (err?.message || t("sanitary:rules.messages.updateFailed")));
    } finally {
      setSaving(false);
    }
  }

  async function deleteCurrentRule() {
    if (!rule || !canEdit || !rule?.usage?.can_delete || saving || deleting) return;
    const confirmed = window.confirm(t("sanitary:rules.messages.deleteConfirm", { name: rule.name }));
    if (!confirmed) return;
    setDeleting(true);
    setError(null);
    setMessage(null);
    try {
      await deleteSanitaryRule(rule.id);
      navigate("/sanitary/rules", { replace: true });
    } catch (err) {
      setError(err?.message || t("sanitary:rules.messages.deleteFailed"));
      setDeleting(false);
    }
  }

  if (loading) {
    return <main className="stock-md-page"><div className="stock-md-card stock-md-empty">{t("sanitary:rules.editor.loading")}</div></main>;
  }

  if (!isNew && !rule) {
    return <main className="stock-md-page"><div className="stock-md-error">{error || t("sanitary:rules.editor.notFound")}</div></main>;
  }

  const fieldDisabled = !canEdit || saving || deleting;
  const versionFieldDisabled = fieldDisabled || versionLocked;
  const canPlanVersion = canEdit && !isNew && !newVersionMode && Boolean(latestVersion?.usage?.used) && rule.status !== SANITARY_RULE_STATUSES.ARCHIVED;
  const canDeleteRule = canEdit && !isNew && !newVersionMode && Boolean(rule?.usage?.can_delete);

  return (
    <main className="stock-md-page sanitary-rule-editor">
      <header className="stock-md-header sanitary-rule-editor__header">
        <div>
          <button type="button" className="sanitary-rule-editor__back" onClick={() => navigate("/sanitary/rules")}>← {t("sanitary:rules.editor.back")}</button>
          <p className="stock-md-eyebrow">{t("sanitary:rules.eyebrow")}</p>
          <h1 className="stock-md-title">
            {isNew
              ? t("sanitary:rules.editor.createTitle")
              : newVersionMode
                ? t("sanitary:rules.editor.newVersionTitle", { name: rule.name })
                : t("sanitary:rules.editor.editTitle", { name: rule.name })}
          </h1>
          <p className="stock-md-subtitle">
            {isNew ? t("sanitary:rules.editor.createSubtitle") : t("sanitary:rules.editor.editSubtitle")}
          </p>
        </div>
        {!isNew && rule ? (
          <div className="sanitary-rule-editor__meta">
            <span className={`sanitary-rule-status sanitary-rule-status--${rule.status}`}>{t(`sanitary:rules.statuses.${rule.status}`, { defaultValue: rule.status })}</span>
            <span>{t("sanitary:rules.editor.versionNumber", { number: latestVersion?.version_no || "—" })}</span>
          </div>
        ) : null}
      </header>

      {!canEdit ? <div className="stock-md-info-box">{t("sanitary:rules.messages.readOnly")}</div> : null}
      {error ? <div className="stock-md-error">{error}</div> : null}
      {message ? <div className="stock-md-success">{message}</div> : null}

      {versionLocked ? (
        <div className="sanitary-rule-editor-lock">
          <div>
            <strong>{t("sanitary:rules.editor.lockedTitle")}</strong>
            <p>{t("sanitary:rules.editor.lockedText", {
              entries: Number(latestVersion?.usage?.consumption_entries || 0),
              events: Number(latestVersion?.usage?.hygiene_events || 0),
              date: latestVersion?.usage?.last_used_business_date || "—",
            })}</p>
          </div>
          {canPlanVersion ? (
            <button type="button" className="stock-md-button stock-md-button--primary" onClick={startNewVersion}>
              {t("sanitary:rules.editor.planNewVersion")}
            </button>
          ) : null}
        </div>
      ) : null}

      {newVersionMode ? (
        <div className="sanitary-rule-editor-version-banner">
          <div>
            <strong>{t("sanitary:rules.editor.newVersionModeTitle")}</strong>
            <p>{t("sanitary:rules.editor.newVersionModeText")}</p>
          </div>
          <button type="button" className="stock-md-button" onClick={cancelNewVersion} disabled={saving}>
            {t("common:actions.cancel", { defaultValue: "Anuluj" })}
          </button>
        </div>
      ) : null}

      <form
        className="sanitary-rule-editor__form"
        onSubmit={(event) => {
          if (isNew) {
            event.preventDefault();
            createRuleWithStatus(SANITARY_RULE_STATUSES.DRAFT);
          } else {
            saveExisting(event);
          }
        }}
      >
        <EditorSection
          number="1"
          title={t("sanitary:rules.editor.sections.basic.title")}
          subtitle={t("sanitary:rules.editor.sections.basic.subtitle")}
        >
          <div className="stock-md-grid stock-md-grid--three">
            <label className="stock-md-field">
              <span>{t("sanitary:rules.form.code")}</span>
              <input value={form.code} onChange={(event) => patch({ code: event.target.value })} disabled={fieldDisabled} />
            </label>
            <label className="stock-md-field stock-md-field--wide">
              <span>{t("sanitary:rules.form.name")}</span>
              <input value={form.name} onChange={(event) => patch({ name: event.target.value })} disabled={fieldDisabled} required />
            </label>
            <label className="stock-md-field">
              <span>{t("sanitary:rules.form.type")}</span>
              <select value={form.rule_type} onChange={(event) => patch({ rule_type: event.target.value })} disabled={fieldDisabled || ruleTypeLocked}>
                {RULE_TYPES.map((value) => <option key={value} value={value}>{t(`sanitary:rules.types.${value}`, { defaultValue: value })}</option>)}
              </select>
              {ruleTypeLocked ? <small>{t("sanitary:rules.editor.identityLocked")}</small> : null}
            </label>
            {!isNew ? (
              <label className="stock-md-field">
                <span>{t("sanitary:rules.form.status")}</span>
                <select value={form.status} onChange={(event) => patch({ status: event.target.value })} disabled={fieldDisabled}>
                  {STATUSES.map((value) => <option key={value} value={value}>{t(`sanitary:rules.statuses.${value}`, { defaultValue: value })}</option>)}
                </select>
              </label>
            ) : (
              <div className="sanitary-rule-editor-inline-note">
                <strong>{t("sanitary:rules.statuses.draft")}</strong>
                <span>{t("sanitary:rules.editor.newRuleDraftHint")}</span>
              </div>
            )}
          </div>
        </EditorSection>

        <EditorSection
          number="2"
          title={t("sanitary:rules.editor.sections.consumption.title")}
          subtitle={t("sanitary:rules.editor.sections.consumption.subtitle")}
        >
          {identityChanged && !identityLocked && (rule?.versions || []).length > 1 ? (
            <div className="stock-md-info-box" role="status">
              {t(form.quantity_basis !== latestVersion?.quantity_basis
                ? "sanitary:rules.editor.basisChangeAllUnusedHint"
                : "sanitary:rules.editor.productChangeAllUnusedHint", { count: rule.versions.length })}
            </div>
          ) : null}
          <div className="stock-md-grid stock-md-grid--three">
            <label className="stock-md-field">
              <span>{t("sanitary:rules.form.quantityBasis")}</span>
              <select
                value={form.quantity_basis}
                onChange={(event) => patch({ quantity_basis: event.target.value })}
                disabled={fieldDisabled || identityLocked}
              >
                <option value={SANITARY_QUANTITY_BASES.DIRECT_PRODUCT}>{t("sanitary:rules.quantityBases.direct_product")}</option>
                <option value={SANITARY_QUANTITY_BASES.WORKING_SOLUTION}>{t("sanitary:rules.quantityBases.working_solution")}</option>
              </select>
              {!isNew ? (
                <small>{t(identityLocked
                  ? (newVersionMode ? "sanitary:rules.editor.identityNewVersionLocked" : "sanitary:rules.editor.identityUsageLocked")
                  : "sanitary:rules.editor.identityUnusedEditable")}</small>
              ) : null}
            </label>

            {!workingSolution ? (
              <>
                <label className="stock-md-field stock-md-field--wide">
                  <span>{t("sanitary:rules.form.product")}</span>
                  <select value={form.product_id} onChange={(event) => patch({ product_id: event.target.value })} disabled={fieldDisabled || identityLocked} required>
                    <option value="">—</option>
                    {products.map((row) => {
                      const product = row.product || row;
                      return <option key={product.id} value={product.id}>{productLabel(product)}</option>;
                    })}
                  </select>
                </label>
                <label className="stock-md-field">
                  <span>{t("sanitary:rules.form.quantity")}</span>
                  <input type="number" step="0.000001" min="0.000001" value={form.quantity} onChange={(event) => patch({ quantity: event.target.value })} disabled={versionFieldDisabled} required />
                </label>
                <label className="stock-md-field">
                  <span>{t("sanitary:rules.form.unit")}</span>
                  <select value={form.unit_code} onChange={(event) => patch({ unit_code: event.target.value })} disabled={versionFieldDisabled} required>
                    <option value="">—</option>
                    {units.map((unit) => <option key={unit.code} value={unit.code}>{unit.name || unit.code} ({unit.symbol || unit.code})</option>)}
                  </select>
                  {selectedProduct?.stock_unit_code ? <small>{t("sanitary:rules.form.stockUnitHint", { unit: selectedProduct.stock_unit_symbol || selectedProduct.stock_unit_code })}</small> : null}
                </label>
              </>
            ) : (
              <>
                <label className="stock-md-field stock-md-field--wide">
                  <span>{t("sanitary:rules.form.solutionDefinition")}</span>
                  <select value={form.solution_definition_id} onChange={(event) => patch({ solution_definition_id: event.target.value, location_id: "" })} disabled={versionFieldDisabled} required>
                    <option value="">—</option>
                    {definitions
                      .filter((definition) => definition.status === "active" || String(definition.id) === String(form.solution_definition_id))
                      .map((definition) => <option key={definition.id} value={definition.id}>{definitionLabel(definition)}</option>)}
                  </select>
                  <small>{t("sanitary:rules.form.solutionDefinitionHelp")}</small>
                </label>
                <label className="stock-md-field">
                  <span>{t("sanitary:rules.form.solutionExecutionCount")}</span>
                  <input type="number" step="0.000001" min="0.000001" value={form.solution_execution_count} onChange={(event) => patch({ solution_execution_count: event.target.value })} disabled={versionFieldDisabled} required />
                </label>
                <div className="stock-md-info-box stock-md-field--wide sanitary-rule-editor__preview">
                  {solutionPreview ? (
                    <>
                      <strong>{t("sanitary:rules.form.solutionPreviewTitle")}</strong>{" "}
                      {formatQty(solutionPreview.executionCount || form.solution_execution_count || 1)} × {solutionPreview.definition?.name || t("sanitary:rules.quantityBases.working_solution")}: {formatQty(solutionPreview.solutionVolume)} {solutionPreview.solutionUnitCode} @ {formatQty(solutionPreview.concentrationPercent)}% → <strong>{formatQty(solutionPreview.concentrateQuantity)} {solutionPreview.concentrateUnitCode}</strong> {t("sanitary:rules.form.concentrate")} + {formatQty(solutionPreview.waterQuantity)} {solutionPreview.waterUnitCode} {t("sanitary:rules.form.water")}.
                    </>
                  ) : solutionPreviewError ? solutionPreviewError : t("sanitary:rules.form.solutionPreviewWaiting")}
                </div>
              </>
            )}
          </div>
        </EditorSection>

        <EditorSection
          number="3"
          title={t("sanitary:rules.editor.sections.validity.title")}
          subtitle={t("sanitary:rules.editor.sections.validity.subtitle")}
        >
          <div className="stock-md-grid stock-md-grid--three">
            <label className="stock-md-field">
              <span>{t("sanitary:rules.form.validFrom")}</span>
              <input type="date" value={form.valid_from} onChange={(event) => patch({ valid_from: event.target.value })} disabled={versionFieldDisabled} required />
            </label>
            <label className="stock-md-field">
              <span>{t("sanitary:rules.form.validTo")}</span>
              <input type="date" value={form.valid_to} onChange={(event) => patch({ valid_to: event.target.value })} disabled={versionFieldDisabled} />
            </label>
            <label className="stock-md-field">
              <span>{t("sanitary:rules.form.calendarPolicy")}</span>
              <select value={form.calendar_policy} onChange={(event) => patch({ calendar_policy: event.target.value })} disabled={versionFieldDisabled}>
                {["working_days_only", "all_days", "manual"].map((value) => <option key={value} value={value}>{t(`sanitary:rules.calendarPolicies.${value}`)}</option>)}
              </select>
            </label>
            {supportsDailyProration(form.rule_type) ? (
              <label className="stock-md-field">
                <span>{t("sanitary:rules.form.dailyProrationMode")}</span>
                <select value={form.daily_proration_mode} onChange={(event) => patch({ daily_proration_mode: event.target.value })} disabled={versionFieldDisabled}>
                  {Object.values(SANITARY_DAILY_PRORATION_MODES).map((value) => <option key={value} value={value}>{t(`sanitary:rules.dailyProrationModes.${value}`)}</option>)}
                </select>
                <small>{t("sanitary:rules.form.dailyProrationModeHelp")}</small>
              </label>
            ) : null}
            {(form.rule_type === SANITARY_RULE_TYPES.PER_VISIT || form.rule_type === SANITARY_RULE_TYPES.PER_SERVICE) ? (
              <label className="stock-md-field">
                <span>{t("sanitary:rules.form.visitStatusPolicy")}</span>
                <select value={form.visit_status_policy} onChange={(event) => patch({ visit_status_policy: event.target.value })} disabled={versionFieldDisabled}>
                  {["completed_or_checked", "checked_only", "completed_only"].map((value) => <option key={value} value={value}>{t(`sanitary:rules.visitStatusPolicies.${value}`)}</option>)}
                </select>
              </label>
            ) : null}
          </div>
        </EditorSection>

        <EditorSection
          number="4"
          title={t("sanitary:rules.editor.sections.trigger.title")}
          subtitle={t("sanitary:rules.editor.sections.trigger.subtitle")}
        >
          {form.rule_type === SANITARY_RULE_TYPES.PER_SERVICE ? (
            <label className="stock-md-field">
              <span>{t("sanitary:rules.form.service")}</span>
              <select value={form.service_id} onChange={(event) => patch({ service_id: event.target.value })} disabled={versionFieldDisabled} required>
                <option value="">—</option>
                {services.map((service) => <option key={service.id} value={service.id}>{[service.name_public, service.service_code ? `(${service.service_code})` : null].filter(Boolean).join(" ")}</option>)}
              </select>
            </label>
          ) : form.rule_type === SANITARY_RULE_TYPES.PER_STATION ? (
            <div className="stock-md-grid stock-md-grid--two">
              <label className="stock-md-field">
                <span>{t("sanitary:rules.form.stationCode")}</span>
                <input value={form.station_code} onChange={(event) => patch({ station_code: event.target.value })} disabled={versionFieldDisabled} required />
              </label>
              <label className="stock-md-field">
                <span>{t("sanitary:rules.form.stationLabel")}</span>
                <input value={form.station_label} onChange={(event) => patch({ station_label: event.target.value })} disabled={versionFieldDisabled} />
              </label>
            </div>
          ) : (
            <div className="sanitary-rule-editor-inline-note">
              <strong>{t(`sanitary:rules.types.${form.rule_type}`, { defaultValue: form.rule_type })}</strong>
              <span>{t("sanitary:rules.editor.triggerAutomatic")}</span>
            </div>
          )}
        </EditorSection>

        <EditorSection
          number="5"
          title={t("sanitary:rules.editor.sections.stock.title")}
          subtitle={t("sanitary:rules.editor.sections.stock.subtitle")}
        >
          <div className="stock-md-grid stock-md-grid--two">
            <label className="stock-md-field">
              <span>{t("sanitary:rules.form.location")}</span>
              <select value={form.location_id} onChange={(event) => patch({ location_id: event.target.value })} disabled={versionFieldDisabled}>
                <option value="">—</option>
                {locations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location)}</option>)}
              </select>
              {workingSolution ? <small>{t("sanitary:rules.form.locationInherited")}</small> : null}
            </label>
            <div className="stock-md-checkbox-row stock-md-checkbox-row--with-help">
              <div className="stock-md-checkbox-option">
                <label className="stock-md-checkbox">
                  <input type="checkbox" checked={form.requires_confirmation} onChange={(event) => patch({ requires_confirmation: event.target.checked })} disabled={fieldDisabled} />
                  {t("sanitary:rules.form.requiresConfirmation")}
                </label>
                <small className="stock-md-checkbox-help">{t("sanitary:rules.form.requiresConfirmationHelp")}</small>
              </div>
              <div className="stock-md-checkbox-option">
                <label className="stock-md-checkbox">
                  <input type="checkbox" checked={form.posts_to_stock} onChange={(event) => patch({ posts_to_stock: event.target.checked })} disabled={fieldDisabled} />
                  {t("sanitary:rules.form.postsToStock")}
                </label>
                <small className="stock-md-checkbox-help">{t("sanitary:rules.form.postsToStockHelp")}</small>
              </div>
            </div>
          </div>
        </EditorSection>

        <EditorSection
          number="6"
          title={t("sanitary:rules.editor.sections.notes.title")}
          subtitle={t("sanitary:rules.editor.sections.notes.subtitle")}
        >
          <div className="stock-md-grid stock-md-grid--two">
            <label className="stock-md-field">
              <span>{t("sanitary:rules.form.comment")}</span>
              <textarea rows="4" value={form.comment} onChange={(event) => patch({ comment: event.target.value })} disabled={fieldDisabled} />
            </label>
            <label className="stock-md-field">
              <span>{t("sanitary:rules.form.versionComment")}</span>
              <textarea rows="4" value={form.version_comment} onChange={(event) => patch({ version_comment: event.target.value })} disabled={versionFieldDisabled} />
            </label>
          </div>
        </EditorSection>

        <div className="sanitary-rule-editor__footer">
          {canDeleteRule ? (
            <button
              type="button"
              className="stock-md-button stock-md-button--dangerish sanitary-rule-editor__delete"
              onClick={deleteCurrentRule}
              disabled={saving || deleting}
            >
              {deleting ? t("sanitary:rules.actions.deleting") : t("sanitary:rules.actions.delete")}
            </button>
          ) : null}
          <button type="button" className="stock-md-button" onClick={() => navigate("/sanitary/rules")} disabled={saving || deleting}>
            {t("common:actions.cancel", { defaultValue: "Anuluj" })}
          </button>
          {canEdit ? (
            isNew ? (
              <>
                <button type="button" className="stock-md-button" onClick={() => createRuleWithStatus(SANITARY_RULE_STATUSES.DRAFT)} disabled={saving || deleting}>
                  {t("sanitary:rules.editor.saveDraft")}
                </button>
                <button type="button" className="stock-md-button stock-md-button--primary" onClick={() => createRuleWithStatus(SANITARY_RULE_STATUSES.ACTIVE)} disabled={saving || deleting}>
                  {t("sanitary:rules.editor.saveAndActivate")}
                </button>
              </>
            ) : (
              <button type="submit" className="stock-md-button stock-md-button--primary" disabled={saving || deleting}>
                {newVersionMode
                  ? t("sanitary:rules.editor.createVersion")
                  : currentVersionEditable
                    ? t("sanitary:rules.editor.saveChanges")
                    : t("sanitary:rules.editor.saveHeader")}
              </button>
            )
          ) : null}
        </div>
      </form>

      {!isNew && rule?.versions?.length ? (
        <details className="stock-md-card sanitary-rule-history">
          <summary>
            <strong>{t("sanitary:rules.editor.historyTitle", { count: rule.versions.length })}</strong>
            <span>{t("sanitary:rules.editor.historyHint")}</span>
          </summary>
          <div className="stock-md-table-wrap">
            <table className="stock-md-table stock-md-table--compact">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("sanitary:rules.table.validity")}</th>
                  <th>{t("sanitary:rules.table.quantity")}</th>
                  <th>{t("sanitary:rules.table.location")}</th>
                  <th>{t("sanitary:rules.overview.columns.usage")}</th>
                  <th>{t("sanitary:rules.editor.historyLastUse")}</th>
                </tr>
              </thead>
              <tbody>
                {rule.versions.map((version) => {
                  const usage = version.usage || {};
                  const count = Number(usage.consumption_entries || 0) + Number(usage.hygiene_events || 0);
                  const quantity = version.quantity_basis === SANITARY_QUANTITY_BASES.WORKING_SOLUTION
                    ? `${formatQty(version.solution_execution_count || 1)} × ${version.solutionDefinition?.name || t("sanitary:rules.quantityBases.working_solution")}`
                    : `${formatQty(version.quantity)} ${version.unit_symbol || version.unit_code || ""}`.trim();
                  return (
                    <tr key={version.id}>
                      <td><strong>v{version.version_no}</strong>{String(version.id) === String(latestVersion?.id) ? <small>{t("sanitary:rules.editor.currentVersion")}</small> : null}</td>
                      <td>{version.valid_from} — {version.valid_to || "∞"}</td>
                      <td>{quantity}</td>
                      <td>{locationLabel(version.location)}</td>
                      <td>{usage.used ? t("sanitary:rules.overview.used", { count }) : t("sanitary:rules.overview.unused")}</td>
                      <td>{usage.last_used_business_date || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      ) : null}
    </main>
  );
}
